import { Helmet } from 'react-helmet-async';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// @mui
import Grid from '@mui/material/Grid2';
import {
	Box,
	Card,
	Table,
	Stack,
	Paper,
	Button,
	Popover,
	TableRow,
	MenuItem,
	NativeSelect,
	TableBody,
	TableCell,
	Container,
	Typography,
	LinearProgress,
	TableContainer,
	TablePagination,
} from '@mui/material';

// components
import { BulkRequestTableRow } from './AdminViewTableRow';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import Dialog from '../../../components/dialog';
import { ListHead, ListToolbar, type HeadCell } from '../../../components/list';
import { Request, BulkRequest, type BulkRequestProps, type Status as RequestStatus } from '../../../models';
import { applySortFilter, getComparator, type Order } from '../../../utils/tableSorter';

import {
	useGetBulkRequestsQuery,
	useUpdateBulkRequestAssessmentDatesMutation,
	useUpdateBulkRequestStatusMutation,
} from '../../../slices/apis/bulkRequestApi';

import { useConfirm } from '../../../hooks/useConfirm';
import { useBulkRequestAssessmentDatePicker } from '../../../hooks/useBulkRequestAssessmentDatePicker';

// ----------------------------------------------------------------------

const TABLE_HEAD: HeadCell[] = [
	// { id: 'arrow', label: '', align: 'left' },
	{ id: 'id', label: 'ID', align: 'left' },
	{ id: 'company', label: 'Company', align: 'left' },
	{ id: 'contact', label: 'Contact', align: 'left' },
	{ id: 'location', label: 'Testing Location', align: 'left' },
	{ id: 'assessmentDate', label: 'Assessment Dates', align: 'left' },
	{ id: 'paymentStatus', label: 'Payment Status', align: 'left' },
	{ id: 'status', label: 'Status', align: 'center' },
	{ id: 'edit', label: '', align: 'left', disableSort: true },
];

const findRequest = (requests: BulkRequestProps[], id: number) => {
	return requests.find((request) => Number(request.id) === id);
};

export default function BulkRequestsAdmin() {
	const { data = [], isFetching } = useGetBulkRequestsQuery();

	const requests = data as BulkRequestProps[];

	const [, setRequestEditable] = useState(false);
	const [page, setPage] = useState(0);
	const [order, setOrder] = useState<Order>('desc');
	const [orderBy, setOrderBy] = useState<keyof BulkRequestProps>('createdAt');
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [archivedRequest /* setArchivedRequest */] = useState(() => ['']);
	const { Confirm, confirmProps } = useConfirm();

	const [filterName, setFilterName] = useState('');
	const [selected] = useState<string[]>([]);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const navigate = useNavigate();
	const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - requests.length) : 0;
	const filteredRequests = applySortFilter({
		list: requests,
		comparator: getComparator(order, orderBy),
		query: filterName,
		orderBy: 'user.firstname',
	});
	const isNotFound = !filteredRequests.length && !!filterName;

	/*
    request assessment date editing
  */
	const [
		updateAssessmentDates,
		{ isSuccess: isUpdateAssessmentDatesSuccess, isLoading: isUpdateAssessmentDatesLoading },
	] = useUpdateBulkRequestAssessmentDatesMutation();

	/*
    assessment date picker
  */
	const { AssessmentDatePicker, pickerProps, openPicker, setAssessmentDates, setReqId, assessmentDates } =
		useBulkRequestAssessmentDatePicker();

	/*
    request deletion
  */
	const [selectedRequest, setSelectedRequest] = useState<BulkRequestProps | null>(null);
	// const [delRequest, { isSuccess: isDelReqSuccess, isLoading: isDelReqLoading }] = useDelRequestMutation();
	const [requestDeleteConfirmDialogOpen, setRequestDeleteConfirmDialogOpen] = useState<boolean>(false);

	const handleOpenAssessmentDateDialog = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const requestId = Number(e.currentTarget.getAttribute('data-request-id'));

			const request = findRequest(requests, requestId);

			if (request) {
				const dates = request.assessmentDates?.map((date) => (date ? new Date(date) : null));
				if (dates !== undefined) {
					setAssessmentDates(dates);
					setReqId(request.id as number);
					setSelectedRequest(request);
					openPicker();
				}
			}
		},
		[requests, openPicker, setAssessmentDates, setReqId],
	);

	const handleNavigate = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			const route = e.currentTarget.getAttribute('data-route');
			if (route) {
				navigate(route);
			}
		},
		[navigate],
	);

	const onAssessmentDateUpdate = () => {
		if (selectedRequest?.id) {
			updateAssessmentDates({
				id: selectedRequest?.id,
				assessmentDates,
			});
		}
	};

	/*
    request status editing
  */
	const [updateRequestStatus, { isSuccess: isUpdateReqStatusSuccess }] = useUpdateBulkRequestStatusMutation();

	let statusOptions = [] as RequestStatus[];

	statusOptions = Request.getAllowedEditableStatusAdmin();

	const handleOpenStatusEditingDialog = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const requestId = Number(e.currentTarget.getAttribute('data-correlation-id'));

			const request = findRequest(requests, requestId);

			if (request) {
				setSelectedRequest(request);
				confirmProps.openConfirm();
			}
		},
		[requests, confirmProps],
	);

	const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!statusOptions.includes(e.target.value as RequestStatus)) {
			return;
		}
		setSelectedRequest({
			...selectedRequest,
			status: e.target.value,
		} as BulkRequestProps);
	};

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		const requestId = Number(event.currentTarget.getAttribute('data-request-id'));

		const request = findRequest(requests, requestId);

		if (request) {
			setAnchorEl(event.currentTarget);
			setSelectedRequest(request);
			setRequestEditable(true);
		}
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof BulkRequestProps) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
		setPage(newPage);
		setTimeout(() => {
			window.scroll({ top: 0, behavior: 'smooth' });
		}, 0);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setRowsPerPage(Number.parseInt(event.target.value, 10));
	};

	const handleFilterByName = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setFilterName(event.target.value);
	};

	const handleEditRequest = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/bulk-requests/${selectedRequest.id}/edit`);
		}
	};

	const handleOpenRequestDetail = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/bulk-requests/${selectedRequest.id}/view`);
		}
	};

	/*
    del request
  */
	const handleDeleteRequest = () => {
		setAnchorEl(null);
		setRequestDeleteConfirmDialogOpen(true);
	};

	const handleCloseRequestDeleteConfirmDialog = () => {
		setRequestDeleteConfirmDialogOpen(false);
	};

	// const onRequestDeleteConfirm = () => {
	//  if (selectedRequest?.id) {
	//    delRequest(selectedRequest.id);
	//  }
	// };

	/*
    handle archived switch
  */
	// const handleArchivedSwitch = () => {
	//  if (archivedRequest.includes('archived')) {
	//    setArchivedRequest(['']);
	//  } else {
	//    setArchivedRequest(['archived']);
	//  }
	// };

	const filterArchivedRequests = (request: BulkRequestProps) => {
		if (archivedRequest.includes('archived')) {
			return true;
		}

		return !request.isArchived;
	};

	useEffect(() => {
		if (isUpdateAssessmentDatesSuccess) {
			pickerProps.closePicker();
			navigate('/dashboard/bulk-requests');
		}

		if (isUpdateReqStatusSuccess) {
			navigate('/dashboard/bulk-requests');
		}
	}, [isUpdateAssessmentDatesSuccess, isUpdateReqStatusSuccess, pickerProps, navigate]);

	return (
		<>
			<Helmet>
				<title> Bulk Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Container maxWidth={false} disableGutters sx={{ px: 1, width: '100%' }}>
				<Stack spacing={4} mb={4}>
					{!isFetching && (
						<Box>
							<Button
								variant="contained"
								data-route="/dashboard/bulk-requests/new"
								onClick={handleNavigate}
								startIcon={<Iconify icon="eva:plus-fill" />}
							>
								New Bulk Request
							</Button>
						</Box>
					)}
				</Stack>

				{isFetching ? (
					<LinearProgress />
				) : (
					<>
						<Card>
							<ListToolbar
								numSelected={selected.length}
								filterName={filterName}
								onFilterName={handleFilterByName}
								placeHolderText={'Bulk request contact...'}
							/>

							<Scrollbar>
								<TableContainer>
									<Table>
										<ListHead
											order={order}
											orderBy={orderBy}
											headLabel={TABLE_HEAD}
											onRequestSort={handleRequestSort}
										/>
										<TableBody>
											{filteredRequests
												.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
												.filter(filterArchivedRequests)
												.map((request) => {
													const { id } = request;

													return (
														<BulkRequestTableRow
															key={id}
															bulkRequest={request}
															handleOpenMenu={handleOpenMenu}
															handleOpenStatusEditingDialog={handleOpenStatusEditingDialog}
															handleOpenAssessmentDateDialog={handleOpenAssessmentDateDialog}
														/>
													);
												})}
											{emptyRows > 0 && (
												<TableRow style={{ height: 53 * emptyRows }}>
													<TableCell colSpan={6} />
												</TableRow>
											)}
										</TableBody>

										{isNotFound && (
											<TableBody>
												<TableRow>
													<TableCell align="center" colSpan={6} sx={{ py: 3 }}>
														<Paper
															sx={{
																textAlign: 'center',
															}}
														>
															<Typography variant="h6" paragraph>
																Not found
															</Typography>

															<Typography variant="body2">
																No results found for &nbsp;
																<strong>&quot;{filterName}&quot;</strong>.
																<br /> Check your typos or use complete words
															</Typography>
														</Paper>
													</TableCell>
												</TableRow>
											</TableBody>
										)}
									</Table>
								</TableContainer>
							</Scrollbar>

							<TablePagination
								rowsPerPageOptions={[10, 20, 50, 100]}
								component="div"
								count={requests.length}
								rowsPerPage={rowsPerPage}
								page={page}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
							/>
						</Card>

						{/* request menu options popover */}
						<Popover
							open={Boolean(anchorEl)}
							onClose={handleCloseMenu}
							anchorEl={anchorEl}
							anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							PaperProps={{
								sx: {
									p: 1,
									width: 140,
									'& .MuiMenuItem-root': {
										px: 1,
										typography: 'body2',
										borderRadius: 0.75,
									},
								},
							}}
						>
							<MenuItem onClick={handleOpenRequestDetail}>
								<Iconify icon={'eva:file-text-outline'} sx={{ mr: 1 }} />
								View
							</MenuItem>

							{!BulkRequest.isCancelled(selectedRequest?.status) && (
								<MenuItem onClick={handleEditRequest}>
									<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
									Edit
								</MenuItem>
							)}

							<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteRequest}>
								<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
								Delete
							</MenuItem>
						</Popover>

						{/* assessment date editing */}
						<AssessmentDatePicker
							{...pickerProps}
							reqId={selectedRequest?.id || 0}
							onConfirm={onAssessmentDateUpdate}
							isUpdateAssessmentDatesLoading={isUpdateAssessmentDatesLoading}
						/>

						{/* status editing */}
						<Confirm
							{...confirmProps}
							title={`Change request ${selectedRequest?.id} status`}
							onConfirm={() => {
								confirmProps.closeConfirm();
								if (selectedRequest) {
									updateRequestStatus({
										id: selectedRequest?.id,
										status: selectedRequest?.status,
									});
								}
							}}
						>
							<Grid container spacing={2}>
								<Grid size={{ md: 12 }}>
									<NativeSelect
										defaultValue={selectedRequest?.status}
										inputProps={{
											name: 'status',
											id: 'uncontrolled-native',
											onChange: onStatusChange,
										}}
									>
										{['--- Please Select ---', ...statusOptions].map((status, index) => (
											<option key={status} value={status}>
												{status}
											</option>
										))}
									</NativeSelect>
								</Grid>
							</Grid>
						</Confirm>

						{/* request delete confirm */}
						<Dialog
							open={Boolean(requestDeleteConfirmDialogOpen)}
							onClose={handleCloseRequestDeleteConfirmDialog}
							title={'Warning'}
						>
							{/* <Grid container spacing={2}>
                <Grid size={{ md: 12 }}>
                  <Typography variant="subtitle2">{`Confirm to delete request ${selectedRequest?.id}`}</Typography>
                </Grid>
                <Grid size={{ md: 6 }}>
                  <Button loading={isDelReqLoading} variant="outlined" color="primary" onClick={onRequestDeleteConfirm}>
                    <span>Confirm</span>
                  </Button>
                </Grid>
                <Grid size={{ md: 6 }}>
                  <Button color="error" variant="outlined" onClick={handleCloseRequestDeleteConfirmDialog}>
                    Cancel
                  </Button>
                </Grid>
              </Grid> */}
						</Dialog>
					</>
				)}
			</Container>
		</>
	);
}
